import { urlForImage } from "@/lib/sanity"
import { cn } from "@/lib/utils"
import type { SanityImage } from "@/types/sanity"
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useReducedMotion } from "framer-motion"
import { type FC, type MouseEvent, useEffect, useState } from "react"

// Each card rotates at its own pace so a grid never changes all at once.
export const PRODUCT_CAROUSEL_INTERVALS = [1450, 1650, 2000, 1850]

type ProductImageCarouselProps = {
  images: SanityImage[]
  alt: string
  width: number
  height: number
  /** Autoplay interval in ms. Omit to disable autoplay. */
  interval?: number
  /** Controlled index (optional). */
  index?: number
  onIndexChange?: (index: number) => void
  labels?: { previous: string; next: string }
  showArrows?: boolean
  className?: string
  imageClassName?: string
}

export const ProductImageCarousel: FC<ProductImageCarouselProps> = ({
  images,
  alt,
  width,
  height,
  interval,
  index: controlledIndex,
  onIndexChange,
  labels = { previous: "Previous image", next: "Next image" },
  showArrows = true,
  className,
  imageClassName,
}) => {
  const [internalIndex, setInternalIndex] = useState(0)
  // Bumped on manual navigation so the autoplay timer restarts.
  const [manualTick, setManualTick] = useState(0)
  const reduceMotion = useReducedMotion()

  const count = images.length
  const current = (controlledIndex ?? internalIndex) % Math.max(count, 1)

  const goTo = (next: number): void => {
    const wrapped = (next + count) % count
    setInternalIndex(wrapped)
    onIndexChange?.(wrapped)
  }

  useEffect(() => {
    if (interval === undefined || count < 2 || reduceMotion === true) {
      return
    }
    const timer = window.setInterval(() => {
      setInternalIndex((previous) => (previous + 1) % count)
    }, interval)
    return () => window.clearInterval(timer)
  }, [interval, count, reduceMotion, manualTick])

  const handleArrow =
    (step: number) =>
    (event: MouseEvent<HTMLButtonElement>): void => {
      // Cards may be wrapped in a link; arrows must not navigate.
      event.preventDefault()
      event.stopPropagation()
      goTo(current + step)
      setManualTick((tick) => tick + 1)
    }

  return (
    <div className={cn("relative overflow-hidden", className)}>
      {images.map((image, i) => (
        <img
          key={i}
          src={urlForImage(image).width(width).height(height).fit("crop").url()}
          alt={i === current ? alt : ""}
          aria-hidden={i !== current}
          loading={i === 0 ? "eager" : "lazy"}
          className={cn(
            "absolute inset-0 w-full h-full object-cover transition-opacity duration-500",
            i === current ? "opacity-100" : "opacity-0",
            imageClassName,
          )}
        />
      ))}

      {showArrows && count > 1 && (
        <>
          <button
            type="button"
            onClick={handleArrow(-1)}
            aria-label={labels.previous}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-black/20 text-white/80 backdrop-blur-sm opacity-60 hover:opacity-100 hover:bg-black/40 focus-visible:opacity-100 transition-opacity"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={handleArrow(1)}
            aria-label={labels.next}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-black/20 text-white/80 backdrop-blur-sm opacity-60 hover:opacity-100 hover:bg-black/40 focus-visible:opacity-100 transition-opacity"
          >
            <FontAwesomeIcon icon={faChevronRight} className="w-3.5 h-3.5" />
          </button>
        </>
      )}
    </div>
  )
}
